import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/api";
import type { ReactNode, HTMLAttributes } from "react";

// Design-system module: small formatter helpers live beside the
// components that use them by design (Lovable's own file layout).
// eslint-disable-next-line react-refresh/only-export-components
export const formatCentsInline = (cents: number) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  }).format(cents / 100);

export function DCard({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { children: ReactNode }) {
  return (
    <div
      className={cn("rounded-[18px] bg-card p-5 shadow-card border border-line/60", className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export function HeroCard({
  eyebrow,
  title,
  children,
  className,
}: {
  eyebrow?: string;
  title: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[26px] bg-grad-hero p-7 shadow-card lg:p-9",
        className,
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-40"
        style={{ backgroundImage: "var(--grad-warm)" }}
      />
      <div className="relative">
        {eyebrow && (
          <p
            className="font-mono text-[10px] uppercase tracking-[0.18em]"
            style={{ color: "var(--dryvn-primary-deep)" }}
          >
            {eyebrow}
          </p>
        )}
        <h1
          className="mt-2 font-display text-4xl leading-[1.05] lg:text-5xl"
          style={{ color: "var(--dryvn-ink)" }}
        >
          {title}
        </h1>
        {children && <div className="mt-4">{children}</div>}
      </div>
    </div>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p
      className="font-mono text-[10px] uppercase tracking-[0.2em]"
      style={{ color: "var(--dryvn-grey)" }}
    >
      {children}
    </p>
  );
}

export function SectionHeader({
  title,
  action,
  eyebrow,
}: {
  title: string;
  eyebrow?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-3 flex items-end justify-between gap-3">
      <div>
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h2 className="font-display text-2xl leading-tight" style={{ color: "var(--dryvn-ink)" }}>
          {title}
        </h2>
      </div>
      {action}
    </div>
  );
}

export function Pill({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "primary" | "life" | "warm" | "muted" | "amber";
}) {
  const styles: Record<string, { bg: string; fg: string }> = {
    neutral: { bg: "var(--dryvn-page-tint)", fg: "var(--dryvn-ink)" },
    primary: {
      bg: "color-mix(in oklab, var(--dryvn-primary) 14%, transparent)",
      fg: "var(--dryvn-primary-deep)",
    },
    life: { bg: "color-mix(in oklab, var(--dryvn-life) 30%, transparent)", fg: "var(--dryvn-ink)" },
    warm: {
      bg: "color-mix(in oklab, var(--dryvn-warm) 30%, transparent)",
      fg: "var(--dryvn-warm-deep)",
    },
    amber: {
      bg: "color-mix(in oklab, var(--dryvn-amber) 22%, transparent)",
      fg: "var(--dryvn-warm-deep)",
    },
    muted: { bg: "var(--dryvn-bg)", fg: "var(--dryvn-grey)" },
  };
  const s = styles[tone];
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ backgroundColor: s.bg, color: s.fg }}
    >
      {children}
    </span>
  );
}

export function ComingSoon() {
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider"
      style={{
        borderColor: "color-mix(in oklab, var(--dryvn-warm) 50%, transparent)",
        color: "var(--dryvn-warm-deep)",
        backgroundColor: "color-mix(in oklab, var(--dryvn-warm) 12%, transparent)",
      }}
    >
      coming soon
    </span>
  );
}

export function Avatar({
  initials,
  color,
  size = 32,
}: {
  initials: string;
  color: string;
  size?: number;
}) {
  return (
    <div
      className="inline-flex shrink-0 items-center justify-center rounded-full font-medium text-white"
      style={{ backgroundColor: color, width: size, height: size, fontSize: size * 0.42 }}
    >
      {initials}
    </div>
  );
}

export function ProgressBar({
  value,
  max,
  tint = "primary",
}: {
  value: number;
  max: number;
  tint?: "primary" | "life" | "warm";
}) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  const color =
    tint === "primary"
      ? "var(--dryvn-primary)"
      : tint === "life"
        ? "var(--dryvn-life)"
        : "var(--dryvn-warm)";
  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full"
      style={{ backgroundColor: "var(--dryvn-page-tint)" }}
    >
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
}

/**
 * Honest first-run guidance. Shown when a hub's query returns genuinely
 * empty (not an error). Always names the SMS path — the always-available
 * way to add data — and, where the hub has a web form, surfaces it via
 * `action` (an "Add" button that opens the form).
 */
export function EmptyState({
  title,
  smsExamples,
  action,
}: {
  title: string;
  smsExamples: string[];
  action?: ReactNode;
}) {
  return (
    <div
      className="rounded-2xl border border-dashed p-6 text-center"
      style={{ borderColor: "var(--dryvn-line)" }}
    >
      <p className="text-sm" style={{ color: "var(--dryvn-ink)" }}>
        {title}
      </p>
      <p className="mt-4 text-xs" style={{ color: "var(--dryvn-grey)" }}>
        Text it to your Dryvn number:
      </p>
      <div className="mt-2 flex flex-wrap justify-center gap-2">
        {smsExamples.map((ex) => (
          <span
            key={ex}
            className="rounded-lg px-3 py-1.5 font-mono text-xs"
            style={{
              backgroundColor: "var(--dryvn-page-tint)",
              color: "var(--dryvn-primary-deep)",
            }}
          >
            “{ex}”
          </span>
        ))}
      </div>
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

/**
 * Honest failure surface. A hub read that errors is NOT the same as empty:
 * a 403 means the account isn't switched on for live data yet (closed
 * beta), anything else is a genuine load failure we own. Never swallow
 * either into a silent empty screen.
 */
export function LoadError({ error }: { error: unknown }) {
  const status = error instanceof ApiError ? error.status : 0;
  const beta = status === 401 || status === 403;
  return (
    <div
      className="rounded-2xl border p-5 text-center"
      style={{
        borderColor: "color-mix(in oklab, var(--dryvn-amber) 45%, transparent)",
        backgroundColor: "color-mix(in oklab, var(--dryvn-amber) 8%, var(--dryvn-card))",
      }}
    >
      <p className="text-sm" style={{ color: "var(--dryvn-ink)" }}>
        {beta
          ? "You're on the early list, but your account isn't switched on for live data yet. We'll let you know the moment it is — in the meantime, texts to your Dryvn number are saved and waiting."
          : "We couldn't load this just now — that's on us, not you. Try again in a moment, or text your Dryvn number and it'll be here when this comes back."}
      </p>
    </div>
  );
}

export function SoftRule() {
  return (
    <div
      className="my-6 h-px w-full"
      style={{
        backgroundImage:
          "linear-gradient(to right, transparent, color-mix(in oklab, var(--dryvn-life) 60%, transparent), transparent)",
      }}
    />
  );
}

export function LineChart({ data, height = 60 }: { data: number[]; height?: number }) {
  if (data.length === 0) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = 100 / (data.length - 1);
  const points = data.map((v, i) => `${i * step},${100 - ((v - min) / range) * 100}`).join(" ");
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full" style={{ height }}>
      <polyline
        points={points}
        fill="none"
        stroke="var(--dryvn-primary)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** Magazine two-column flowing grid for desktop; single column on mobile. */
export function MagazineGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("grid grid-cols-1 gap-5 lg:grid-cols-6", className)}>{children}</div>;
}

export function Confetti({ show }: { show: boolean }) {
  if (!show) return null;
  const pieces = Array.from({ length: 18 });
  const colors = [
    "var(--dryvn-primary)",
    "var(--dryvn-life)",
    "var(--dryvn-warm)",
    "var(--dryvn-amber)",
  ];
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((_, i) => (
        <span
          key={i}
          className="absolute h-1.5 w-1.5 rounded-sm"
          style={{
            left: `${(i * 97) % 100}%`,
            top: "-5%",
            backgroundColor: colors[i % colors.length],
            animation: `dryvn-confetti ${1.2 + (i % 6) * 0.15}s ease-out forwards`,
            animationDelay: `${(i % 8) * 0.05}s`,
          }}
        />
      ))}
    </div>
  );
}
